import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type DiaEspelho, type Espelho } from "../api";
import PedirCorrecao from "./PedirCorrecao";

/**
 * O ESPELHO DO COLABORADOR — o mesmo mês que o DP imprime.
 *
 * Existe por um motivo prático: conferir antes de assinar. Quando o espelho só
 * aparecia no papel, no fim do mês, lembrar do dia 3 já era impossível — então
 * todo mundo assinava e reclamava depois, quando não havia mais o que fazer.
 *
 * As contas vêm do servidor, com a MESMA régua do DP. Se aqui aparecesse um
 * número e lá outro, a conversa viraria a palavra de um contra a do outro.
 *
 * Conferir só serve se der para CORRIGIR: tocar num dia abre o pedido de
 * correção (o "esqueci de bater") e o envio do atestado daquele dia, já com a
 * data escolhida — ver PedirCorrecao.tsx. Nada disso muda a folha sozinho.
 */

/** 8.5 -> "8h30"; negativo mantém o sinal. */
function horasLegiveis(v: number | null | undefined): string {
  if (v == null) return "—";
  const sinal = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  const h = Math.floor(abs);
  const min = Math.round((abs - h) * 60);
  return min === 0 ? `${sinal}${h}h` : `${sinal}${h}h${String(min).padStart(2, "0")}`;
}

function mesAtual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

function mesPorExtenso(mes: string): string {
  const [ano, m] = mes.split("-").map(Number);
  return `${MESES[m - 1] ?? mes} de ${ano}`;
}

/** Mês anterior/seguinte de "AAAA-MM". */
function deslocarMes(mes: string, passo: number): string {
  const [ano, m] = mes.split("-").map(Number);
  const d = new Date(Date.UTC(ano, m - 1 + passo, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export default function MeuEspelho() {
  const [mes, setMes] = useState(mesAtual());
  const [espelho, setEspelho] = useState<Espelho | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [diaAberto, setDiaAberto] = useState<DiaEspelho | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      setEspelho(await api.meuEspelho(mes));
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível carregar o espelho.");
    }
  }, [mes]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // O pedido é de um dia; depois de enviado, o mês inteiro é relido para o dia
  // já voltar marcado como "aguardando o DP".
  async function pedidoEnviado(mensagem: string) {
    setDiaAberto(null);
    setAviso(mensagem);
    await carregar();
    window.scrollTo(0, 0);
  }

  if (diaAberto) {
    return (
      <PedirCorrecao
        dia={diaAberto}
        onEnviado={pedidoEnviado}
        onFechar={() => setDiaAberto(null)}
      />
    );
  }

  return (
    <div className="espelho">
      <div className="espelho-navegacao">
        <button
          className="botao-mes"
          onClick={() => setMes(deslocarMes(mes, -1))}
          aria-label="Mês anterior"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className="espelho-mes">{mesPorExtenso(mes)}</span>
        <button
          className="botao-mes"
          onClick={() => setMes(deslocarMes(mes, 1))}
          // Sem sentido navegar para o futuro: não há ponto batido lá.
          disabled={mes >= mesAtual()}
          aria-label="Mês seguinte"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {aviso && <div className="aviso-ok">{aviso}</div>}
      {erro && <div className="aviso-erro">{erro}</div>}

      {!espelho ? (
        <div className="carregando">Carregando…</div>
      ) : (
        <>
          <div className="resumo">
            <div className="resumo-item">
              <span>Trabalhado</span>
              <strong>{horasLegiveis(espelho.totais.horasTrabalhadas)}</strong>
            </div>
            <div className="resumo-item">
              <span>Previsto</span>
              <strong>{horasLegiveis(espelho.totais.horasPrevistas)}</strong>
            </div>
            <div className="resumo-item">
              <span>Para o banco</span>
              <strong>{horasLegiveis(espelho.totais.extrasParaBanco)}</strong>
            </div>
            <div className="resumo-item">
              <span>Extra em folha</span>
              <strong>{horasLegiveis(espelho.totais.extrasParaFolha)}</strong>
            </div>
          </div>

          <ul className="dias">
            {espelho.dias.map((d) => {
              const vazio = !d.registrado;
              return (
                <li
                  key={d.data}
                  className={`dia ${d.fimDeSemana || d.feriado ? "dia-descanso" : ""} ${
                    d.futuro ? "dia-futuro" : ""
                  }`}
                >
                  {/* O dia inteiro é o botão: no celular, alvo pequeno em lista
                      longa é toque errado. Dia futuro não abre — não há o que
                      corrigir em um dia que ainda não aconteceu. */}
                  <button
                    type="button"
                    className="dia-botao"
                    onClick={() => setDiaAberto(d)}
                    disabled={d.futuro}
                  >
                    <span className="dia-cabeca">
                      <span className="dia-numero">{String(d.dia).padStart(2, "0")}</span>
                      <span className="dia-semana">{d.diaSemana}</span>
                      {d.feriado && <span className="dia-feriado">{d.feriado}</span>}
                      <span className="dia-horas">{horasLegiveis(d.horas)}</span>
                      {!d.futuro && (
                        <span className="dia-seta" aria-hidden="true">
                          ›
                        </span>
                      )}
                    </span>

                    {vazio ? (
                      <span className="dia-vazio">
                        {d.futuro ? "—" : d.feriado ? d.feriado : d.situacao}
                      </span>
                    ) : (
                      <span className="dia-marcacoes">
                        <span>{d.entrada ?? "--:--"}</span>
                        <span>{d.saida_almoco ?? "--:--"}</span>
                        <span>{d.retorno_almoco ?? "--:--"}</span>
                        <span>{d.saida ?? "--:--"}</span>
                      </span>
                    )}

                    {d.extras > 0 && (
                      <span className="dia-nota">
                        {horasLegiveis(d.extras)}{" "}
                        {d.destino === "banco" ? "para o banco de horas" : "de hora extra"}
                      </span>
                    )}
                    {d.faltantes > 0 && (
                      <span className="dia-nota">{horasLegiveis(d.faltantes)} abaixo da jornada</span>
                    )}

                    {d.pedido && (
                      <span
                        className={`dia-selo ${
                          d.pedido.status === "Pendente"
                            ? "dia-selo-aguardando"
                            : d.pedido.status === "Aprovada"
                              ? "dia-selo-ok"
                              : "dia-selo-recusado"
                        }`}
                      >
                        {d.pedido.tipo === "atestado" ? "Atestado" : "Correção"}
                        {d.pedido.status === "Pendente"
                          ? " aguardando o DP"
                          : d.pedido.status === "Aprovada"
                            ? " aprovada"
                            : " recusada"}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          <p className="espelho-rodape">
            Faltou uma batida ou tem atestado para entregar? <strong>Toque no dia</strong> e mande
            o pedido. Ele vale depois que o DP aprovar — o registro original da sua batida fica
            guardado do mesmo jeito.
          </p>
        </>
      )}
    </div>
  );
}
